import { lazy, Suspense, useCallback, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { ConversationsList, If, ImagePreviewPanel, NoConversationMsg } from ".."
import ConversationsThread from "../conversations-thread"
import ConversationLeadDetailsPanel from "../conversation-lead-details-panel"
import PillTabs from "../pill-tabs"
import { Threads } from "../../../types"
import { ConversationsListSkeleton } from "../skeletons"
import { ConversationsThreadSkeleton } from "../skeletons"

const AdvisorConversationsList = lazy(
  () => import("../advisor-conversations-list")
)
const AdvisorThread = lazy(() => import("../advisor-thread"))

type ViewTab = "sms" | "advisor"

const VIEW_TABS = [
  { id: "sms" as const, label: "SMS" },
  { id: "advisor" as const, label: "AI Advisor" }
]

interface Props {
  isLoading: boolean
  threads: Threads
  filteredThreads: Threads
  hasMore: boolean
  loadMore: () => void
  isLoadingMore: boolean
  hasNoSmsThreads: boolean
}

const ConversationsWithListAndThread = ({
  isLoading,
  threads,
  filteredThreads,
  hasMore,
  loadMore,
  isLoadingMore,
  hasNoSmsThreads
}: Props) => {
  const { _id = "" } = useParams<{ _id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeView = (searchParams.get("view") as ViewTab) || "sms"
  const [selectedAdvisorSessionId, setSelectedAdvisorSessionId] = useState("")

  const handleViewChange = useCallback(
    (tab: string) => {
      const next = tab as ViewTab
      setSearchParams(prev => {
        if (next === "sms") {
          prev.delete("view")
        } else {
          prev.set("view", next)
        }
        return prev
      })
    },
    [setSearchParams]
  )

  const isSms = activeView === "sms"

  return (
    <>
      <div className="flex flex-1 overflow-hidden h-full">
        {/* Sidebar: tabs + list */}
        <div className="w-screen sm:w-[367px] min-w-full sm:min-w-[367px] sm:max-w-[367px] flex flex-col bg-white border-r border-neutral-200">
          <div className="shrink-0 border-b border-neutral-200 px-4 pt-3 pb-2">
            <PillTabs
              tabs={VIEW_TABS}
              currentTab={activeView}
              onChange={handleViewChange}
              className="justify-center"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {isSms ? (
              <ConversationsList
                filteredThreads={filteredThreads}
                isLoading={isLoading}
                hasMore={hasMore}
                loadMore={loadMore}
                isLoadingMore={isLoadingMore}
              />
            ) : (
              <Suspense fallback={<ConversationsListSkeleton />}>
                <AdvisorConversationsList
                  selectedSessionId={selectedAdvisorSessionId}
                  onSelectSession={setSelectedAdvisorSessionId}
                />
              </Suspense>
            )}
          </div>
        </div>

        {/* Right panel */}
        {isSms ? (
          hasNoSmsThreads ? (
            <NoConversationMsg />
          ) : _id ? (
            <div
              className={`bg-[#F8F8F8] flex flex-col overflow-y-auto min-w-full w-screen sm:w-full transition-transform sm:min-w-0 z-20 ${
                _id ? "-translate-x-full sm:transform-none" : "translate-x-0"
              }`}
            >
              <ConversationsThread threads={threads} />
            </div>
          ) : (
            <If
              condition={!isLoading}
              then={
                <div className="bg-[#F8F8F8] flex-col items-center justify-center w-full hidden md:flex">
                  <p>Select a thread to view messages.</p>
                </div>
              }
            />
          )
        ) : (
          <div className="bg-[#F8F8F8] flex flex-col overflow-y-auto min-w-full w-screen sm:w-full sm:min-w-0">
            <Suspense fallback={<ConversationsThreadSkeleton />}>
              <AdvisorThread sessionId={selectedAdvisorSessionId} />
            </Suspense>
          </div>
        )}
      </div>
      <ImagePreviewPanel />
      <ConversationLeadDetailsPanel />
    </>
  )
}

export default ConversationsWithListAndThread
